import moment from 'moment'
import createLocationMainSearchItemEntity from '@/services/entities/location-main-search-item.js'
import { getNestedValue } from '@/utils/object.js'
import * as R from 'ramda'

/**
 * Parses APIV2 response (by_vehicule_dates) into flat array.
 *
 * @example
 *  Input: apiv2_response.data.items
 *  Output: Array of histories (vehicle)
 * @param {Object} responseData
 * @returns
 */
export function getItemsFromHistoryVehicleDriverAPIV2Response(responseData) {
  return Object.keys(responseData).reduce((array, dateString) => {
    array = array.concat(responseData[dateString] || [])
    return array
  }, [])
}

/**
 * Parses APIV3 response into flat array
 * @param responseData
 * @returns {*[]}
 */
export function getItemsFromHistoryVehicleDriverAPIV3Response(responseData) {
  return Object.keys(responseData).reduce((array, dateString) => {
    const dateEntry = responseData[dateString]

    if (!dateEntry || typeof dateEntry !== 'object') {
      return array
    }

    const date = dateEntry.date || dateString

    if (!Array.isArray(dateEntry.vehicles)) {
      return array // Skip if there are no vehicles
    }

    dateEntry.vehicles
      .filter(
        (vehicle) =>
          !(
            vehicle.sectionsNumber === 0 &&
            vehicle.linestrings &&
            vehicle.linestrings.length === 0
          )
      ) //Filter out vehicles without troncon and without linestrings. Allows to display vehicles with only linestrings (#48014)
      .forEach((vehicle) => {
        array.push({ date, ...vehicle })
      })

    return array
  }, [])
}

/**
 * Parses APIV2 response (by_chauffeur_dates) into flat array.
 *
 * @example
 *  Input: apiv2_response.data.items
 *  Output: Array of histories (driver)
 * @param {Object} responseData
 * @returns
 */
export function getItemsFromHistoryDriverAPIV2Response(responseData) {
  let result = []
  Object.keys(responseData).forEach((dateString) => {
    Object.keys(responseData[dateString]).forEach((driverId) => {
      const driver = responseData[dateString][driverId]

      if (!driver || !Array.isArray(driver.historique)) {
        console.log('no driver')
        return // Skip if historique is missing or not an array
      }

      driver.historique.forEach((historyDetails) => {
        result.push({
          driverName: driver.nom_chauffeur,
          driverId,
          ...historyDetails,
        })
      })
    })
  })
  return result
}

/**
 * Parses APIV2 response (by_circuit_dates) into flat array.
 *
 * Note: Response can contain multiple "historique" (circuit executions) for a single date/circuit_id
 *
 * @example
 *  Input: apiv2_response.data.items
 *  Output: Array of histories (circuit)
 * @param {Object} responseData
 * @returns
 */
export function getItemsFromHistoryCircuitAPIV2Response(responseData) {
  //Reduce executions from different dates into single array
  return Object.keys(responseData).reduce((array, dateString) => {
    let circuitExecutions = []

    //Loop circuits
    Object.keys(responseData[dateString]).forEach((circuitId) => {
      let historiesArray = responseData[dateString][circuitId].historique || {}
      //Loop executions
      Object.keys(historiesArray).forEach((executionId) => {
        circuitExecutions.push({
          //Headers
          ...R.omit(['historique'], responseData[dateString][circuitId]),
          //Execution data
          ...(historiesArray[executionId] || {}),
        })
      })
    })

    array = array.concat(circuitExecutions)
    return array
  }, [])
}

/**
 *
 * Parses Location module item from main search
 * Item comes from API (temp reel / historique)
 *
 * @TODO Refactor/Move into services/entities/location-main-search-entity
 *
 * @param {*} item
 * @param {*} options
 * @param {*} rootGetters
 * @returns
 */
export function parseLocationItem(item, options, rootGetters) {
  // Handle nested values from APIV2 and APIV3
  const vehicleId = getNestedValue(item, [
    'vehicule.id',
    'vehicle.id',
    'vehicule_id',
    'vehicleId',
  ])

  const vehicleCategory = getVehicleCategoryObject(vehicleId, rootGetters)

  return createLocationMainSearchItemEntity(item, {
    ...options,
    vehicleCategory,
  })
}

/**
 * @TODO Refactor/Improve
 *
 * Get vehicle category object from search module loaded dataset
 *
 * @param {*} vehicleId
 * @param {*} rootGetters
 * @returns
 */
export function getVehicleCategoryObject(vehicleId, rootGetters) {
  const vehicle = rootGetters['search_module/getVehicles'].find(
    (v) => v.id.toString() == vehicleId.toString()
  )

  if (!vehicle) {
    //Vue.$log.warn("Can't find vehicle object", vehicleId);
    return null
  }

  let categoryObject = rootGetters['search_module/getVehicleCategories'].find(
    (c) => {
      return c.id.toString() == vehicle.categoryId
    }
  )

  if (!categoryObject) {
    return null
  }

  return {
    ...categoryObject,
    parent: categoryObject.parent || categoryObject.parent_id,
  }
}

/**
 *
 * Helper to compute querystring date parameters (v2 historique APIs) from Array of date ranges.
 *
 * @param {[]} selectedDateRanges Array of date ranges from search_module
 * @param isFirstAndLastDay
 * @param shouldUseApiV3
 * @returns
 */
export function getHistoryAPIQueryStringDatePart(
  selectedDateRanges = [],
  isFirstAndLastDay,
  shouldUseApiV3 = false
) {
  let querystring = ''
  let dates = selectedDateRanges.map((range) => {
    return range[0]
  })
  if (dates.length > 0) {
    let timeFrom = moment(
      selectedDateRanges[selectedDateRanges.length - 1][0]
    ).format('HH:mm')
    let timeTo = moment(
      selectedDateRanges[selectedDateRanges.length - 1][1]
    ).format('HH:mm')
    dates = dates.map((d) => moment(d).format(`YYYY-MM-DD`))
    dates = dates.filter((d, i) => dates.indexOf(d) === i) //Remove duplicates

    const dateParamName = shouldUseApiV3 ? 'dates[]' : 'dates'

    const dateQuery = shouldUseApiV3
      ? dates.map((d) => `${dateParamName}=${d}`).join('&')
      : `${dateParamName}=${dates.join(',')}`

    querystring += `&${dateQuery}`

    querystring += `&${
      shouldUseApiV3 ? 'startTime' : 'heure_debut'
    }=${window.encodeURI(timeFrom)}`
    querystring += `&${
      shouldUseApiV3 ? 'endTime' : 'heure_fin'
    }=${window.encodeURI(timeTo)}`
    querystring += `&${
      shouldUseApiV3
        ? 'applyHourOnlyOnFirstAndLastDate'
        : 'premier_dernier_jour'
    }=${isFirstAndLastDay}`
  }
  return querystring
}
